import React from "react";
import { useInfiniteQuery, useQueryClient, useQuery } from "react-query";
import {
  addNewTrade,
  fetchTotalTradeSum,
  fetchTrades,
  fetchAllCompany,
  fetchAllCrypto,
  deleteTrade,
  updateTrade,
} from "../api";
import { useNotifications } from "../helpers/useNotifications";
import Table from "../components/Table/Table";
import moment from "moment/moment";
import formatNumber from "../helpers/formatNumber";
import Grid from "../components/Grid/Grid";
import LoadingButton from "../components/LoadingButton/LoadingButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquarePlus } from "@fortawesome/free-solid-svg-icons";
import Modal from "../components/Modal/Modal";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import Spinner from "../components/Spinner/Spinner";
import Image from "../components/Image/Image";

function Trades({ title }) {
  const createNotification = useNotifications();
  const queryClient = useQueryClient();
  const { currentUser } = useAuth;
  const limit = 25;
  const [showTradeModal, setShowTradeModal] = React.useState(false);
  const [showTradeEdit, setShowTradeEdit] = React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(undefined);

  const [type, setType] = React.useState(selectedRow?.type || "buy");
  const [date, setDate] = React.useState(selectedRow?.date || "");
  const [investment, setInvestment] = React.useState(
    selectedRow?.investment || ""
  );
  const [share, setShare] = React.useState(selectedRow?.share || "");
  const [hint, setHint] = React.useState(selectedRow?.hint || "");

  const { data: allCompany } = useQuery(["allCompany"], () =>
    fetchAllCompany()
  );
  const { data: allCrypto } = useQuery(["allCrypto"], () => fetchAllCrypto());

  React.useEffect(() => {
    if (selectedRow) {
      setType(selectedRow?.type);
      setDate(selectedRow?.date);
      setInvestment(selectedRow?.investment);
      setShare(selectedRow?.share);
      setHint(selectedRow?.hint || "");
    }
  }, [selectedRow]);

  //* load total sum of trades
  const {
    data: dbTotalTrades,
    isLoading,
    isError,
    error,
  } = useQuery(["myTotalTrades"], fetchTotalTradeSum);

  //* load trades by limit
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(
      ["mytrades"],
      async ({ pageParam = 0 }) => {
        return fetchTrades(pageParam, limit);
      },
      {
        getNextPageParam: (lastPage, allPages) => {
          // Wenn weniger als limit Elemente kamen, ist Schluss
          if (lastPage.length < limit) return undefined;
          return allPages.length; // nächste page
        },
        // 👇 Deine zusätzlichen Optionen
        staleTime: 1000 * 60 * 5, // 5 Minuten Frischezeit
        cacheTime: 1000 * 60 * 10, // 10 Minuten im Cache
        refetchOnWindowFocus: false, // Kein Refetch bei Fokus
        refetchOnMount: false, // Kein Refetch beim Mount
        enabled: currentUser?.authenticated,
      }
    );

  const allItems = data?.pages.flat() || [];

  if (isLoading) return <Spinner />;
  if (isError) createNotification("error", error.message);

  const columns = [
    {
      label: "",
      field: "ticker",
      colWidth: "75%",
      rowValue: (_, row) => (
        <div className="dividend-payment-container">
          <div className="dividend-payment-image-container">
            <Link
              to={`/blog/${row.company.category}/${row.ticker}`}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={
                  row.company?.logo ||
                  `https://assets.parqet.com/logos/symbol/${row.ticker}`
                }
                alt={row.ticker}
                className="table-thumbnail"
                height={45}
                width={45}
                style={{ marginRight: 8 }}
              />
            </Link>
          </div>
          <div className="dividend-payment-text-container">
            <div className="dividend-payment-date text-muted">
              <span className="trades-share-badge badge badge-info">
                {formatNumber(row?.share)}
              </span>
              {moment(row?.date).format("DD.MM.YYYY")}
            </div>
            <div className="dividend-payment-name text-light">
              {row.company?.name || row?.ticker}
            </div>
          </div>
        </div>
      ),
    },
    {
      label: "",
      field: "investment",
      colWidth: "25%",
      rowValue: (val, row) => (
        <div className="d-flex align-items-center p-3 justify-content-end">
          {row?.type === "buy" ? (
            <span
              className={`badge ${
                val === "0" && row?.hint
                  ? "bg-dividend-type text-muted"
                  : "dividend-payment-value  "
              }`}
            >
              {val === "0" && row?.hint ? row.hint : `+ ${formatNumber(val)} €`}
            </span>
          ) : (
            <span
              className={`badge ${
                val === "0" && row?.hint
                  ? "bg-dividend-type text-muted"
                  : "badge-negative  "
              }`}
            >
              {val === "0" && row?.hint ? row.hint : `- ${formatNumber(val)} €`}
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <h1 className="text-light text-center">{title}</h1>
      <h3 className="text-center stockinfo-keyfacts-value text-lawngreen">
        {`${formatNumber(dbTotalTrades?.calculatedInvestment)} €`}
      </h3>

      <div className="article-main">
        <Grid>
          <h5 className="text-light text-center">
            Trades{" "}
            <span className="dividend-payment-subtitle text-muted">
              ({dbTotalTrades?.totalTrades})
            </span>
            <button
              className="btn btn-link dismiss-active-or-focus delete-form-label"
              data-toggle="modal"
              data-target="#addTrade"
              onClick={(e) => {
                e.preventDefault();
                setShowTradeModal((prev) => !prev);
              }}
            >
              <FontAwesomeIcon icon={faSquarePlus} />
            </button>
          </h5>

          <Table
            data={allItems}
            columns={columns}
            onClick={(row) => {
              setShowTradeEdit(true);
              setSelectedRow(row);
            }}
          />
        </Grid>
      </div>
      {hasNextPage && (
        <div className="text-center mt-6">
          <LoadingButton
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
          />
        </div>
      )}

      <Modal
        id="addTrade"
        visible={showTradeModal}
        setVisible={setShowTradeModal}
        title="Trade hinzufügen"
        onSave={(formData) => {
          if (
            formData?.type &&
            formData?.ticker &&
            formData?.date &&
            formData?.value &&
            formData?.share
          ) {
            addNewTrade(
              formData?.type,
              formData?.ticker,
              formData?.date,
              formData?.value,
              formData?.share,
              formData?.hint || ""
            )
              .then((result) => {
                createNotification("success", result.message);
                queryClient.invalidateQueries(["mytrades"]);
              })
              .catch((err) => {
                createNotification("error", err.message);
              });
          } else {
            createNotification("error", "Bitte alle Daten angeben");
          }
        }}
      >
        <div className="form-group col-12">
          <label className="text-dark" for="type">
            Typ
          </label>
          <div className="input-group">
            <select id="type" name="type" className="form-select">
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="ticker">
            Ticker
          </label>
          <div className="input-group">
            <select
              className="form-control"
              id="ticker"
              name="ticker"
              data-live-search="true"
              showContent="true"
            >
              <optgroup label="Aktien">
                {allCompany?.map((el) => (
                  <option key={el?.ticker} value={el?.ticker}>
                    {el?.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Krypto">
                {allCrypto?.map((el) => (
                  <option key={el?.ticker} value={el?.ticker}>
                    {el?.name}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="date">
            Datum
          </label>
          <div className="input-group">
            <input type="date" className="form-control" id="date" name="date" />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="value">
            Summe
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="value"
              name="value"
            />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="share">
            Anteile
          </label>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              id="share"
              name="share"
            />
          </div>
        </div>

        <div className="form-group col-12">
          <label className="text-dark" for="hint">
            Hinweis
          </label>
          <div className="input-group">
            <input type="text" className="form-control" id="hint" name="hint" />
          </div>
        </div>
      </Modal>

      <div
        className={`offcanvas offcanvas-bottom article-secondary-mobile ${
          showTradeEdit && "article-secondary-visible show"
        }`}
        tabIndex="-1"
        id="offcanvasBottom"
        aria-labelledby="offcanvasLabel"
      >
        <div className="offcanvas-header">
          <button
            type="button"
            className="btn-close-custom dismiss-active-or-focus"
          >
            <i
              className="icon-click icon-close bi bi-x-lg text-muted"
              onClick={() => setShowTradeEdit((prev) => !prev)}
            />
          </button>
        </div>
        <div className="offcanvas-body">
          <div className="article-secondary-info">
            {selectedRow?.company?.logo && (
              <Image
                source={selectedRow?.company?.logo}
                className="article-logo"
              />
            )}

            <h4 className="text-dark text-muted text-truncate">
              {selectedRow?.company?.name}
            </h4>

            <div
              id="data-description"
              className="text-muted text-left article-base-description-active"
            >
              <form id="updateTrade">
                <div className="form-group col-12">
                  <label className="text-dark" for="type">
                    Typ
                  </label>
                  <div className="input-group">
                    <select
                      id="type"
                      name="type"
                      className="form-select"
                      value={type}
                      onChange={(e) => setType(e.target.value)}
                    >
                      <option value="buy">Buy</option>
                      <option value="sell">Sell</option>
                    </select>
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="date">
                    Datum
                  </label>
                  <div className="input-group">
                    <input
                      type="date"
                      className="form-control"
                      id="date"
                      name="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="value">
                    Summe
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="value"
                      name="value"
                      value={investment}
                      onChange={(e) => setInvestment(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="share">
                    Anteile
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="share"
                      name="share"
                      value={share}
                      onChange={(e) => setShare(e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-group col-12">
                  <label className="text-dark" for="hint">
                    Hinweis
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="hint"
                      name="hint"
                      value={hint}
                      onChange={(e) => setHint(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ marginLeft: "15px" }}>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    style={{ marginRight: "5px" }}
                    onClick={() => {
                      deleteTrade(selectedRow?.ticker, selectedRow?.date)
                        .then((result) => {
                          createNotification("success", result.message);
                          queryClient.invalidateQueries(["mytrades"]);
                          setShowTradeEdit(false);
                        })
                        .catch((err) => {
                          createNotification("error", err.message);
                        });
                    }}
                  >
                    Löschen
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-search-custom dismiss-active-or-focus"
                    onClick={(e) => {
                      e.preventDefault(); //

                      if (type && date && investment && share) {
                        updateTrade(
                          type,
                          selectedRow?.ticker,
                          selectedRow?.date,
                          date,
                          investment,
                          share,
                          hint || ""
                        )
                          .then((result) => {
                            createNotification("success", result.message);
                            queryClient.invalidateQueries(["mytrades"]);
                            setShowTradeEdit(false);
                          })
                          .catch((err) => {
                            createNotification("error", err.message);
                          });
                      } else {
                        createNotification("error", "Bitte alle Daten angeben");
                      }
                    }}
                  >
                    Speichern
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Trades;
